"use strict";

/**
 * DGF-4614 — the "converts to 3 buttons at the bottom" half of the mobile express sticky bar.
 *
 * Fades the bar out while the bag's own checkout block is in view, so the shopper never sees two
 * sets of the same buttons.
 */

const BAR_SELECTOR = ".js-express-sticky-bar";
// The bag's own checkout block, in the totals column. Deliberately scoped to `.totals`: the bare
// class also matches `.checkout-continue.mobile` at the top of cart.isml, which is a THIRD copy of the
// CTA and is not what the bar should defer to — deferring to it would hide the bar at the top of the
// page, which is exactly where DGF-4614 wants Apple Pay to be visible.
const IN_PAGE_BLOCK_SELECTOR = ".totals .checkout-continue";
const HIDDEN_CLASS = "express-sticky-bar--hidden";
// DGF-5012: the sticky site header, which slides back over the top of the page on scroll up.
const HEADER_SELECTOR = ".header-container";

/**
 * Fades the bar out while the bag's own checkout block is on screen.
 *
 * No-ops rather than falling back to a scroll listener when IntersectionObserver is missing. The bar
 * simply stays visible, which is a correct and complete state — the shopper can still check out and
 * still use Apple Pay, they just see the bar over the block for the last part of the scroll. A scroll
 * handler on a page with the bag's line-item DOM is a worse trade than that.
 *
 * @returns {void}
 */
function initExpressStickyBar() {
    const bar = document.querySelector(BAR_SELECTOR);

    if (!bar || typeof window.IntersectionObserver !== "function") {
        return;
    }

    const inPageBlock = document.querySelector(IN_PAGE_BLOCK_SELECTOR);

    // An empty bag renders neither.
    if (!inPageBlock) {
        return;
    }

    // DGF-5012: a block covered by the header isn't in view, so the bar mustn't hide for it.
    const header = document.querySelector(HEADER_SELECTOR);
    let headerHeight = header ? header.offsetHeight : 0;

    /**
     * A new observer per header height, since rootMargin can't change after creation.
     * @returns {Object} the IntersectionObserver, already observing the block
     */
    function observeBlock() {
        const blockObserver = new window.IntersectionObserver(function onIntersect(entries) {
            entries.forEach(function applyEntry(entry) {
                bar.classList.toggle(HIDDEN_CLASS, entry.isIntersecting);
            });
        }, { rootMargin: `-${headerHeight}px 0px 0px 0px` });

        blockObserver.observe(inPageBlock);
        return blockObserver;
    }

    let observer = observeBlock();

    // Header height can change after load (fonts, rotation); scroll show/hide only moves it, so this won't fire on scroll.
    if (header && typeof window.ResizeObserver === "function") {
        new window.ResizeObserver(function onHeaderResize() {
            if (header.offsetHeight === headerHeight) {
                return;
            }
            headerHeight = header.offsetHeight;
            observer.disconnect();
            observer = observeBlock();
        }).observe(header);
    }
}

module.exports = {
    BAR_SELECTOR: BAR_SELECTOR,
    IN_PAGE_BLOCK_SELECTOR: IN_PAGE_BLOCK_SELECTOR,
    HIDDEN_CLASS: HIDDEN_CLASS,
    HEADER_SELECTOR: HEADER_SELECTOR,
    initExpressStickyBar: initExpressStickyBar
};
